import { useParams } from 'react-router-dom'
import { Badge } from '@/components/ui/badge'
import { Layers, Box, ExternalLink } from 'lucide-react'
import { TextCompareIcon } from '@/components/icons'

interface WorkItemCardData {
  id?: string
  number: number
  title: string
  epic?: string | null
  feature?: string | null
}

interface WorkItemCardsProps {
  data: string
}

export function WorkItemCards({ data }: WorkItemCardsProps) {
  const { projectId } = useParams()

  let items: WorkItemCardData[]
  try {
    items = JSON.parse(data)
    if (!Array.isArray(items)) throw new Error('Not an array')
  } catch {
    return (
      <pre className="rounded-lg bg-muted p-4 text-sm text-muted-foreground overflow-x-auto">
        {data}
      </pre>
    )
  }

  const handleClick = (item: WorkItemCardData) => {
    if (!projectId) return
    const url = item.id
      ? `/projects/${projectId}/backlog?workItemId=${encodeURIComponent(item.id)}`
      : `/projects/${projectId}/backlog`
    window.open(url, '_blank')
  }

  return (
    <div className="my-4 space-y-3">
      {items.map((item) => (
        <button
          key={item.id ?? item.number}
          type="button"
          onClick={() => handleClick(item)}
          aria-label={`View work item PBI-${item.number}: ${item.title}`}
          className="group relative flex w-full cursor-pointer flex-col gap-3 rounded-xl border border-border bg-card p-4 text-left shadow-sm transition-all hover:shadow-md hover:border-primary/30 hover:-translate-y-0.5"
        >
          <ExternalLink className="absolute top-3 right-3 h-3.5 w-3.5 text-muted-foreground/0 transition-colors group-hover:text-muted-foreground/60" />

          <div className="flex items-center gap-3">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <TextCompareIcon className="h-5 w-5" />
            </span>
            <span className="min-w-0 flex-1 text-sm font-semibold text-foreground line-clamp-2 leading-snug">
              {item.title}
            </span>
          </div>

          {(item.epic || item.feature) && (
            <div className="flex flex-wrap items-center gap-1.5">
              {item.epic && (
                <Badge variant="secondary" className="gap-1 text-[11px] font-medium">
                  <Layers className="h-3 w-3" />
                  {item.epic}
                </Badge>
              )}
              {item.feature && (
                <Badge variant="outline" className="gap-1 text-[11px] font-medium">
                  <Box className="h-3 w-3" />
                  {item.feature}
                </Badge>
              )}
            </div>
          )}
        </button>
      ))}
    </div>
  )
}
